import { computed, ref, watch } from 'vue';
import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { useShowSubPopup } from '../popups.uses';
import { useInternational } from '@/common/locale';
import router from '@/common/router';

export interface Props {
  menuInfo: MenuInfo | null;
  dashboardMenuList?: MenuInfo[];
  analysisBoardMenuList?: MenuInfo[];
}

export const setup = (props: Props) => {
  const { t } = useInternational();
  const subMenuInfo = ref<MenuInfo | null>(null);
  const clickedMenu = ref<string | null>(null);
  const favoriteCategoryText = t('WORD.FAVORITE');
  const regularCategoryText = t('WORD.ITEMS');

  const { mainMenuRef, subMenuRef, subMenuPopupStyle, setHoveredItemPosition } = useShowSubPopup();

  const computedDashboardMenu = computed<MenuInfo | null>(() => {
    if (!props.menuInfo?.dashboardList) {
      return {
        text: t('WORD.GNB.DASHBOARD'),
        value: BASE_MENU_VALUE.DASHBOARD,
        subMenuList: getBoardMenuList([]),
      };
    }
    return {
      ...props.menuInfo?.dashboardList,
      subMenuList: getBoardMenuList(props.dashboardMenuList ?? []),
    };
  });
  const computedAnalysisBoardMenu = computed<MenuInfo | null>(() => {
    if (!props.menuInfo?.analysisBoardList) {
      return null;
    }
    return {
      ...props.menuInfo.analysisBoardList,
      subMenuList: getBoardMenuList(props.analysisBoardMenuList ?? []),
    };
  });
  const computedSubMenuList = computed<MenuInfo[]>(() => {
    if (!subMenuInfo.value) {
      return [];
    }
    if (subMenuInfo?.value?.value === BASE_MENU_VALUE.DASHBOARD) {
      return computedDashboardMenu.value?.subMenuList ?? [];
    }
    if (subMenuInfo.value.value === BASE_MENU_VALUE.ANALYSISBOARD) {
      return computedAnalysisBoardMenu.value?.subMenuList ?? [];
    }
    return subMenuInfo.value.subMenuList ?? [];
  });

  const getMidCategoryTitle = (title: string) => {
    return title === subMenuInfo.value?.text ? '' : title;
  };

  const clearSubMenu = (forceClear?: boolean) => {
    if (!!clickedMenu.value && !forceClear) {
      return;
    }
    clickedMenu.value = null;
    subMenuInfo.value = null;
  };

  const getBoardMenuList = (menuList: MenuInfo[]) => {
    const favoriteItems: MenuInfo[] = [];
    const regularItems: MenuInfo[] = [];
    menuList.forEach((item) => {
      if (item.favoriteId) {
        favoriteItems.push(item);
      } else {
        regularItems.push(item);
      }
    });
    const subMenuList: MenuInfo[] = [];
    if (favoriteItems.length > 0) {
      subMenuList.push({ text: favoriteCategoryText, value: 'favorite', children: favoriteItems });
    }
    if (regularItems.length > 0) {
      subMenuList.push({ text: regularCategoryText, value: 'regular', children: regularItems });
    }
    return subMenuList;
  };

  const setSubMenu = (e: MouseEvent, menuInfo: MenuInfo) => {
    setHoveredItemPosition(e);
    subMenuInfo.value = menuInfo;
  };

  const onClickSubMenu = (e?: MouseEvent, menuInfo?: MenuInfo | null) => {
    if (!e || !menuInfo?.subMenuList?.length) {
      clearSubMenu();
      return;
    }
    if (clickedMenu.value === menuInfo.value) {
      clearSubMenu(true);
      return;
    }
    clickedMenu.value = menuInfo.value;
    setSubMenu(e, menuInfo);
  };

  const onHoverSubMenu = (e?: MouseEvent, menuInfo?: MenuInfo | null) => {
    if (clickedMenu.value) {
      // 클릭 된 메뉴가 있을 경우 호버 불가
      return;
    }
    if (!e || (menuInfo?.value !== BASE_MENU_VALUE.DASHBOARD && !menuInfo?.subMenuList?.length)) {
      clearSubMenu();
      return;
    }
    setSubMenu(e, menuInfo);
  };

  const onClickGoToDashboardList = () => {
    const currentRoute = router.currentRoute.value;
    const isAlreadyOnDashboardList = currentRoute.name === 'dashboard_Dashboard List';

    if (!isAlreadyOnDashboardList) {
      router.push({ name: 'dashboard_Dashboard List' });
    }
    clearSubMenu();
  };
  watch(
    () => props.menuInfo,
    () => {
      clearSubMenu(true);
    },
  );

  return {
    mainMenuRef,
    subMenuRef,
    subMenuPopupStyle,

    subMenuInfo,
    clickedMenu,
    computedDashboardMenu,
    computedAnalysisBoardMenu,
    computedSubMenuList,

    getMidCategoryTitle,

    onHoverSubMenu,
    onClickSubMenu,
    onClickGoToDashboardList,
    clearSubMenu,
    t,
  };
};
